import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { AppointmentService } from '../../core/services/appointment.service';
import { CalendarService } from '../../core/services/calendar.service';
import { Appointment } from '../../core/models/appointment.model';
import { Router } from '@angular/router';

type FilterTab = 'today' | 'week' | 'all';
type ViewMode  = 'list' | 'timeline';

@Component({
  selector: 'app-professional',
  standalone: false,
  templateUrl: './professional.component.html',
  styleUrl: './professional.component.scss'
})
export class ProfessionalComponent implements OnInit, OnDestroy {
  activeFilter: FilterTab = 'today';
  viewMode: ViewMode = 'list';
  searchQuery = '';
  allAppointments: Appointment[] = [];
  isLoading = true;
  private sub = new Subscription();

  constructor(
    public auth: AuthService,
    private apptSvc: AppointmentService,
    private calendarSvc: CalendarService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const user = this.auth.currentUserSnapshot;
    if (!user) { this.router.navigate(['/login']); return; }

    this.sub.add(
      this.apptSvc.byProfessional$(user.id).subscribe(apts => {
        this.allAppointments = apts.sort((a, b) =>
          (a.date + a.time).localeCompare(b.date + b.time)
        );
        this.isLoading = false;
      })
    );
  }

  get filteredAppointments(): Appointment[] {
    const today = new Date().toISOString().split('T')[0];
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() + 7);
    const weekEndStr = weekEnd.toISOString().split('T')[0];

    let result: Appointment[];
    switch (this.activeFilter) {
      case 'today': result = this.allAppointments.filter(a => a.date === today); break;
      case 'week':  result = this.allAppointments.filter(a => a.date >= today && a.date <= weekEndStr); break;
      default:      result = this.allAppointments;
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      result = result.filter(a =>
        a.clientName.toLowerCase().includes(q) ||
        a.clientEmail.toLowerCase().includes(q) ||
        a.serviceType.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /** Group filtered appointments by date for display */
  get groupedAppointments(): { date: string; label: string; items: Appointment[] }[] {
    const map = new Map<string, Appointment[]>();
    for (const apt of this.filteredAppointments) {
      if (!map.has(apt.date)) map.set(apt.date, []);
      map.get(apt.date)!.push(apt);
    }
    return Array.from(map.entries()).map(([date, items]) => ({
      date,
      label: this.formatDateFull(date),
      items,
    }));
  }

  get pendingCount(): number {
    return this.allAppointments.filter(a => a.status === 'pending').length;
  }

  get confirmedTodayCount(): number {
    const today = new Date().toISOString().split('T')[0];
    return this.allAppointments.filter(a => a.date === today && a.status === 'confirmed').length;
  }

  confirm(apt: Appointment): void {
    this.apptSvc.confirm(apt.id);
    this.calendarSvc.downloadIcs({ ...apt, status: 'confirmed' });
  }

  cancel(id: string): void {
    this.apptSvc.cancel(id);
  }

  addToCalendar(apt: Appointment): void {
    this.calendarSvc.downloadIcs(apt);
  }

  openGoogle(apt: Appointment): void {
    this.calendarSvc.openGoogleCalendarLink(apt);
  }

  formatDateFull(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0,0,0,0);
    const target = new Date(d);
    target.setHours(0,0,0,0);
    const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Mañana';
    return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
  }

  /** Timeline rows: hours from 08:00 to 20:00 in 30-min slots */
  get todayTimeline(): { time: string; appointment: Appointment | null }[] {
    const today = new Date().toISOString().split('T')[0];
    const todayApts = this.allAppointments.filter(a => a.date === today);
    const rows: { time: string; appointment: Appointment | null }[] = [];
    for (let h = 8; h < 20; h++) {
      for (const m of [0, 30]) {
        const time = `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
        const apt = todayApts.find(a => a.time === time && a.status !== 'cancelled') ?? null;
        rows.push({ time, appointment: apt });
      }
    }
    return rows;
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
